import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useSessionContext } from "@/contexts/SessionContext";
import { useTaskWatchingContext } from "@/contexts/TaskWatchingContext";
import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";
import { isInviteResponseTask } from "../../invites/helpers/inviteTaskHelpers";
import { useRequesterOptions } from "../../booking/hooks/useRequesterOptions";
import type { RequesterMode } from "../../booking/types";
import { practitionerRoleReference } from "../../invites/helpers/practitionerRoleReferences";
import {
  listAffiliateTeleconsultorias,
  type AffiliateTeleconsultoriaRow,
} from "../services/affiliateTeleconsultoriasService";
import { cancelAffiliateBooking } from "../services/cancelAffiliateBooking";

const EMPTY_ROWS: AffiliateTeleconsultoriaRow[] = [];

/**
 * Teleconsultorias marcadas pela afiliada (com o status do convite).
 * Visão profissional (self): as do usuário. Visão da clínica (choose): as de todos.
 * `refreshKey` muda depois de uma nova marcação para recarregar.
 * `filters.startDate/endDate` (opcional, YYYY-MM-DD): período; sem ele, de hoje em diante.
 */
export function useAffiliateTeleconsultorias(
  refreshKey: number,
  filters?: { requesterMode?: RequesterMode; startDate?: string; endDate?: string },
) {
  const { access } = useSessionContext();
  const { options, self } = useRequesterOptions(filters?.requesterMode);
  const startDate = filters?.startDate;
  const endDate = filters?.endDate;
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const practitionerReferences = useMemo(
    () => options.map((option) => option.reference).join(","),
    [options],
  );
  // Convites são enviados pelo PractitionerRole do solicitante na afiliada.
  const requesterRoleReferencesKey = useMemo(
    () =>
      options
        .map((option) =>
          practitionerRoleReference({
            organizationId: access?.organizationId || "",
            practitionerReference: option.reference,
          }),
        )
        .filter(Boolean)
        .join(","),
    [options, access?.organizationId],
  );

  const loadTask = useAsyncTask({
    task: useCallback(
      () =>
        listAffiliateTeleconsultorias({
          practitionerReferences,
          requesterRoleReferences: requesterRoleReferencesKey
            ? requesterRoleReferencesKey.split(",")
            : [],
          startDate,
          endDate,
        }),
      [practitionerReferences, requesterRoleReferencesKey, startDate, endDate],
    ),
    initialData: EMPTY_ROWS,
  });
  const cancelTask = useAsyncTask({ task: cancelAffiliateBooking });

  const { run } = loadTask;
  const reload = useCallback(() => {
    void run().catch((error) => console.error("useAffiliateTeleconsultorias", error));
  }, [run]);

  // Resposta do teleconsultor (aceitou/recusou) chega como notificação via SSE:
  // recarrega para o status da reserva mudar na hora.
  const { allTasks } = useTaskWatchingContext();
  const responsesKey = useMemo(
    () =>
      (Array.isArray(allTasks) ? allTasks : [])
        .filter((task) => isInviteResponseTask(task))
        .map((task) => task.id)
        .join(","),
    [allTasks],
  );

  useEffect(() => {
    if (practitionerReferences) reload();
  }, [practitionerReferences, refreshKey, reload, responsesKey]);

  const { run: runCancel } = cancelTask;
  const cancelBooking = useCallback(
    async (row: AffiliateTeleconsultoriaRow) => {
      setCancellingId(row.appointmentId);
      try {
        await runCancel({ appointmentId: row.appointmentId, latestTask: row.invite.latestTask });
        toast.info("Reserva cancelada. O horário voltou a ficar livre.");
        reload();
      } catch (error) {
        console.error("useAffiliateTeleconsultorias.cancelBooking", error);
        toast.error("Não foi possível cancelar a reserva.");
      } finally {
        setCancellingId(null);
      }
    },
    [runCancel, reload],
  );

  return {
    rows: loadTask.data ?? EMPTY_ROWS,
    isLoading: loadTask.loading,
    hasError: loadTask.status === "error",
    selfReference: self.reference,
    /** Solicitantes possíveis (filtro por profissional na visão da clínica). */
    requesterOptions: options,
    cancellingId,
    cancelBooking,
    reload,
  };
}
